<script setup lang="ts">
import { ref, computed } from 'vue';
import Button from '@/components/ui/UIButton.vue';
import Badge from '@/components/ui/Badge.vue';
import { Upload, Clock, X, Download, Eye } from '@lucide/vue';
import type { Document } from '@/types/ui/PromptManager';
import { aiService } from '@/services/AIService';
import DocumentViewerModal from '@/components/DocumentViewerModal.vue';

interface Props {
  selectedDocument: Document | null;
  documents: Document[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  documentSelect: [document: Document | null];
  'update:documents': [documents: Document[]];
}>();

const isDragOver = ref(false);
const fileInput = ref<HTMLInputElement>();
const showDocumentModal = ref(false);
const viewDocumentFileName = ref('');
const viewDocument = ref<Document | undefined>(undefined);

const documents = computed({
  get: () => props.documents,
  set: (value) => emit('update:documents', value),
});

const handleDragOver = () => {
  isDragOver.value = true;
};

const handleDragLeave = () => {
  isDragOver.value = false;
};

const handleDrop = (e: DragEvent) => {
  isDragOver.value = false;

  const files = Array.from(e.dataTransfer?.files || []);
  handleFileUpload(files);
};

const handleFileSelect = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const files = Array.from(target.files || []);
  handleFileUpload(files);
};

const handleFileUpload = async (files: File[]) => {
  const uploads = files.map(async (file) => {
    const s3Key = await aiService.uploadDocument(file);
    const newDocument: Document = {
      name: file.name,
      size: file.size,
      createdAt: new Date().toISOString(),
      type: file.type,
      s3Key: s3Key,
    } as Document;

    return newDocument;
  });

  const newDocuments = await Promise.all(uploads); // wait for all uploads to finish
  documents.value = [...newDocuments, ...documents.value];
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFileIcon = (type?: string) => {
  if (!type) return '📄';
  if (type.includes('pdf')) return '📄';
  if (type.includes('word') || type.includes('document') || type.includes('docx')) return '📝';
  if (type.includes('text')) return '📄';
  return '📁';
};

const handleDeleteDocument = async (doc: Document, event: Event) => {
  event.stopPropagation();

  const confirmed = confirm(`Are you sure you want to delete "${doc.name}"?`);
  if (!confirmed) return;

  try {
    await aiService.deleteDocument(doc.s3Key!);
    documents.value = documents.value.filter((d) => d.s3Key !== doc.s3Key);
    // Clear selection if deleted document was selected
    if (props.selectedDocument?.s3Key === doc.s3Key) {
      emit('documentSelect', null);
    }
  } catch (error) {
    console.error('Failed to delete document:', error);
    alert('Failed to delete document. Please try again.');
  }
};

const downloadDocument = async (doc: Document, event: Event) => {
  event.stopPropagation();

  if (!doc.s3Key) {
    console.error('Document s3Key is required for download');
    return;
  }

  try {
    const downloadUrl = await aiService.downloadDocument(doc.s3Key);

    if (!downloadUrl) {
      throw new Error('No download URL received from server');
    }

    const anchorElement = document.createElement('a');
    anchorElement.href = downloadUrl;
    anchorElement.download = doc.name || 'download';
    anchorElement.setAttribute('target', '_blank');
    anchorElement.click();
    anchorElement.remove();
  } catch (error: any) {
    console.error('Failed to download document:', error);
    alert('Failed to download document. Please try again.');
  }
};

const fetchDocument = async (convertToPdf: boolean): Promise<string> => {
  if (!viewDocument.value) {
    throw new Error('No document selected for viewing');
  }

  try {
    return await aiService.downloadDocument(viewDocument.value.s3Key!, convertToPdf);
  } catch (error: any) {
    console.error('Failed to download document:', error);
    throw error;
  }
};

const openDocument = async (doc: Document, event: Event) => {
  event.stopPropagation();

  if (!doc.s3Key) {
    console.error('Document s3Key is required for viewing');
    return;
  }

  viewDocumentFileName.value = doc.name || 'undefined';
  viewDocument.value = doc;
  showDocumentModal.value = true;
};

const handleDocumentClick = (doc: Document) => {
  // Toggle selection if clicking the same document
  if (props.selectedDocument?.s3Key === doc.s3Key) {
    emit('documentSelect', null);
  } else {
    emit('documentSelect', doc);
  }
};

const isDocumentSelected = (doc: Document) => {
  return props.selectedDocument?.s3Key === doc.s3Key;
};
</script>

<template>
  <div class="space-y-4">
    <!-- Upload Area -->
    <div
      :class="[
        'rounded-lg border-2 border-dashed p-6 text-center transition-all duration-200',
        isDragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400',
      ]"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop">
      <Upload :class="['mx-auto mb-2 h-8 w-8', isDragOver ? 'text-blue-500' : 'text-slate-400']" />
      <p class="mb-2 text-sm text-slate-600">Drop files here or click to upload</p>
      <p class="mb-3 text-xs text-slate-500">Supports .pdf, .docx, .txt files</p>
      <input
        type="file"
        multiple
        accept=".pdf,.docx,.txt"
        @change="handleFileSelect"
        class="hidden"
        ref="fileInput" />
      <Button
        variant="outline"
        size="sm"
        @click="fileInput?.click()">
        <Upload class="mr-2 h-4 w-4" />
        Choose Files
      </Button>
    </div>

    <!-- Document List -->
    <div
      v-if="documents.length > 0"
      class="space-y-2">
      <h4 class="text-sm font-medium text-slate-700">Stored Documents</h4>
      <div class="max-h-64 space-y-2 overflow-y-auto">
        <div
          v-for="(doc, index) in documents"
          :key="index"
          :class="[
            'group cursor-pointer rounded-lg border p-3 transition-all duration-200',
            isDocumentSelected(doc)
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
          ]"
          @click="handleDocumentClick(doc)">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="text-xl">{{ getFileIcon(doc.type) }}</span>
              <div>
                <p class="max-w-48 truncate text-sm font-medium text-slate-900">
                  {{ doc.name }}
                </p>
                <div class="mt-1 flex items-center gap-2">
                  <Badge
                    v-if="doc.size"
                    variant="outline"
                    class="text-xs">
                    {{ formatFileSize(doc.size) }}
                  </Badge>
                  <span class="flex items-center gap-1 text-xs text-slate-500">
                    <Clock class="h-3 w-3" />
                    {{ formatDate(doc.createdAt || '') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <Badge
                v-if="isDocumentSelected(doc)"
                variant="secondary">
                Selected
              </Badge>
              <button
                @click="openDocument(doc, $event)"
                class="rounded-md p-1 text-slate-500 opacity-0 transition-all duration-200 group-hover:opacity-100 hover:bg-blue-50 hover:text-blue-600"
                title="View document">
                <Eye class="h-5 w-5" />
              </button>
              <button
                @click="downloadDocument(doc, $event)"
                class="rounded-md p-1 text-slate-400 opacity-0 transition-all duration-200 group-hover:opacity-100 hover:bg-blue-50 hover:text-blue-600"
                title="Download document">
                <Download class="h-5 w-5" />
              </button>
              <button
                @click="handleDeleteDocument(doc, $event)"
                class="rounded-md p-1 text-slate-400 opacity-0 transition-all duration-200 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600"
                title="Delete document">
                <X class="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <DocumentViewerModal
    :show="showDocumentModal"
    :fileName="viewDocumentFileName"
    :fetchDocument="fetchDocument"
    @update:show="showDocumentModal = $event"
    @close="showDocumentModal = false" />
</template>
